import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, Play, Send, Mail, Webhook } from "lucide-react";

export function ApiSchedulesCard() {
  const [items, setItems] = useState([]);
  const [datasets, setDatasets] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/api-schedules").then(({ data }) => setItems(data || [])).catch(() => {});
  useEffect(() => {
    load();
    api.get("/datasets").then(({ data }) => setDatasets(data || [])).catch(() => {});
  }, []);
  const del = async (id) => { if (!window.confirm("Delete schedule?")) return; try { await api.delete(`/api-schedules/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const runNow = async (id) => {
    try {
      const { data } = await api.post(`/api-schedules/${id}/run`);
      if (data.status === "sent") toast.success(`Ran now: ${data.detail}`);
      else toast.error(`Run failed: ${data.detail}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    // Always refresh so the Last-run line reflects the history entry, whether
    // the run succeeded, failed gracefully or raised.
    load();
  };

  return (
    <Card className="glass-card border-0 p-6" data-testid="api-schedules-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-5 h-5 text-[color:var(--brand-ocean)]" />
          <h3 className="font-display text-lg text-[color:var(--ink)]">Scheduled API reports</h3>
        </div>
        <Button data-testid="api-schedule-new-btn" size="sm" onClick={() => { setEditing(null); setOpen(true); }} className="bg-[color:var(--brand-ocean)] text-white">
          <Plus className="w-4 h-4 mr-1" />New
        </Button>
      </div>
      <p className="text-sm text-[color:var(--ink-3)] mt-2">Cron-driven CSV or JSON exports pushed to a partner webhook or emailed to a distribution list. Runs use your row-level security.</p>

      {items.length === 0 ? (
        <div data-testid="api-schedules-empty" className="mt-4 text-xs text-[color:var(--ink-3)] italic bg-[color:var(--surface-2)] rounded-lg px-3 py-4 text-center">
          No schedules yet — hit &quot;New&quot; to add one.
        </div>
      ) : (
        <div className="mt-4 space-y-1.5">
          {items.map((s) => (
            <div key={s.id} data-testid={`api-schedule-row-${s.id}`} className="rounded-lg bg-[color:var(--surface-2)] px-3 py-2 text-xs">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate flex-1">
                  <span className="font-medium text-[color:var(--ink)]">{s.name}</span>
                  <span className="text-[color:var(--ink-3)] ml-2">· {s.dataset_name}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className="text-[10px] uppercase font-semibold text-[color:var(--brand-ocean)] flex items-center gap-1">
                    {s.delivery === "webhook" ? <Webhook className="w-3 h-3" /> : <Mail className="w-3 h-3" />}
                    {s.delivery}
                  </span>
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[color:var(--surface)] text-[color:var(--ink-2)]">{s.format?.toUpperCase()}</span>
                  <span className="text-[10px] text-[color:var(--ink-3)]">{s.frequency}</span>
                  <button data-testid={`api-schedule-run-${s.id}`} onClick={() => runNow(s.id)} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--brand-mint)]" title="Run now"><Play className="w-3.5 h-3.5" /></button>
                  <button data-testid={`api-schedule-edit-${s.id}`} onClick={() => { setEditing(s); setOpen(true); }} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--brand-ocean)]" title="Edit"><Send className="w-3.5 h-3.5" /></button>
                  <button data-testid={`api-schedule-del-${s.id}`} onClick={() => del(s.id)} className="p-1 text-[color:var(--ink-3)] hover:text-[color:var(--danger)]" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                </span>
              </div>
              {s.last_status && (
                <div className="mt-1 text-[10px] text-[color:var(--ink-3)]">
                  Last run: <span className={s.last_status === "sent" ? "text-emerald-600" : "text-amber-600"}>{s.last_status}</span>
                  {s.last_detail && <span> — {s.last_detail}</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <ScheduleDialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }} datasets={datasets} initial={editing} onSaved={load} />
    </Card>
  );
}

function ScheduleDialog({ open, onOpenChange, datasets, initial, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState("");
  const [datasetId, setDatasetId] = useState("");
  const [dimensions, setDimensions] = useState("");   // comma sep field names
  const [measures, setMeasures] = useState("");       // "field:sum, field2:avg"
  const [filters, setFilters] = useState("");         // "field=value; field2=value2"
  const [limit, setLimit] = useState(5000);
  const [format, setFormat] = useState("csv");
  const [frequency, setFrequency] = useState("daily");
  const [hour, setHour] = useState(8);
  const [weekday, setWeekday] = useState(0);
  const [delivery, setDelivery] = useState("email");
  const [recipients, setRecipients] = useState("");
  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookHeaders, setWebhookHeaders] = useState(""); // "Key: value" per line
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name || "");
      setDatasetId(initial.dataset_id || "");
      setDimensions((initial.dimensions || []).map((d) => d.field).join(", "));
      setMeasures((initial.measures || []).map((m) => `${m.field}:${m.agg || "sum"}`).join(", "));
      setFilters((initial.filters || []).map((f) => `${f.field}=${Array.isArray(f.value) ? f.value.join("|") : f.value}`).join("; "));
      setLimit(initial.limit || 5000);
      setFormat(initial.format || "csv");
      setFrequency(initial.frequency || "daily");
      setHour(initial.hour_utc ?? 8);
      setWeekday(initial.weekday ?? 0);
      setDelivery(initial.delivery || "email");
      setRecipients((initial.recipients || []).join(", "));
      setWebhookUrl(initial.webhook_url || "");
      setWebhookHeaders(Object.entries(initial.webhook_headers || {}).map(([k, v]) => `${k}: ${v}`).join("\n"));
      setEnabled(initial.enabled !== false);
    } else {
      setName(""); setDatasetId(""); setDimensions(""); setMeasures(""); setFilters("");
      setLimit(5000); setFormat("csv"); setFrequency("daily"); setHour(8); setWeekday(0);
      setDelivery("email"); setRecipients(""); setWebhookUrl(""); setWebhookHeaders(""); setEnabled(true);
    }
  }, [open, initial]);

  const parseDims = (s) => s.split(",").map((x) => x.trim()).filter(Boolean).map((f) => ({ field: f }));
  const parseMeasures = (s) => s.split(",").map((x) => x.trim()).filter(Boolean).map((piece) => {
    const [field, agg] = piece.split(":").map((x) => x.trim());
    return { field, agg: agg || "sum" };
  });
  const parseFilters = (s) => s.split(";").map((x) => x.trim()).filter(Boolean).map((piece) => {
    const [field, ...vparts] = piece.split("=");
    const value = vparts.join("=").trim();
    return { field: field.trim(), op: "eq", value };
  });
  const parseHeaders = (s) => Object.fromEntries(s.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => {
    const i = l.indexOf(":"); return i < 0 ? [l, ""] : [l.slice(0, i).trim(), l.slice(i + 1).trim()];
  }));

  const save = async () => {
    if (!name.trim() || !datasetId) return toast.error("Name and dataset are required");
    setBusy(true);
    try {
      const payload = {
        name: name.trim(), dataset_id: datasetId,
        dimensions: parseDims(dimensions), measures: parseMeasures(measures), filters: parseFilters(filters),
        limit: Math.max(1, Math.min(Number(limit) || 5000, 50000)),
        format, frequency, hour_utc: Number(hour) || 8, weekday: Number(weekday) || 0,
        delivery,
        recipients: recipients.split(",").map((r) => r.trim()).filter(Boolean),
        webhook_url: webhookUrl.trim() || null,
        webhook_headers: parseHeaders(webhookHeaders),
        enabled,
      };
      if (isEdit) await api.patch(`/api-schedules/${initial.id}`, payload);
      else await api.post("/api-schedules", payload);
      toast.success(isEdit ? "Schedule updated" : "Schedule created");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const dsCols = useMemo(() => {
    const ds = datasets.find((d) => d.id === datasetId);
    return (ds?.schema || ds?.columns || []).map((c) => c.name || c);
  }, [datasets, datasetId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><CalendarClock className="w-4 h-4" />{isEdit ? "Edit" : "New"} scheduled API report</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Name</Label>
            <Input data-testid="api-sched-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nightly sales feed" className="bg-[color:var(--surface)]" />
          </div>
          <div><Label className="text-xs">Dataset</Label>
            <Select value={datasetId} onValueChange={setDatasetId}>
              <SelectTrigger data-testid="api-sched-dataset" className="bg-[color:var(--surface)]"><SelectValue placeholder="Pick a dataset" /></SelectTrigger>
              <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
            {dsCols.length > 0 && <div className="text-[10px] text-[color:var(--ink-3)] mt-1">Columns: {dsCols.slice(0, 12).join(" · ")}{dsCols.length > 12 ? "…" : ""}</div>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Dimensions</Label>
              <Input data-testid="api-sched-dims" value={dimensions} onChange={(e) => setDimensions(e.target.value)} placeholder="country, product" className="bg-[color:var(--surface)]" />
            </div>
            <div><Label className="text-xs">Measures (field:agg)</Label>
              <Input data-testid="api-sched-meas" value={measures} onChange={(e) => setMeasures(e.target.value)} placeholder="amount:sum, qty:sum" className="bg-[color:var(--surface)]" />
            </div>
          </div>
          <div><Label className="text-xs">Filters (field=value; …)</Label>
            <Input data-testid="api-sched-filters" value={filters} onChange={(e) => setFilters(e.target.value)} placeholder="status=won; country=US" className="bg-[color:var(--surface)]" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Format</Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="api-sched-format" className="bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="csv">CSV</SelectItem><SelectItem value="json">JSON</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger data-testid="api-sched-freq" className="bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Limit</Label>
              <Input data-testid="api-sched-limit" type="number" min={1} max={50000} value={limit} onChange={(e) => setLimit(e.target.value)} className="bg-[color:var(--surface)]" />
            </div>
          </div>
          {frequency !== "hourly" && (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Hour (UTC)</Label>
                <Input type="number" min={0} max={23} value={hour} onChange={(e) => setHour(e.target.value)} className="bg-[color:var(--surface)]" />
              </div>
              {frequency === "weekly" && (
                <div><Label className="text-xs">Weekday</Label>
                  <Select value={String(weekday)} onValueChange={setWeekday}>
                    <SelectTrigger className="bg-[color:var(--surface)]"><SelectValue /></SelectTrigger>
                    <SelectContent>{["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
          <div><Label className="text-xs">Delivery</Label>
            <div className="flex gap-2 mt-1">
              <Button data-testid="api-sched-delivery-email" variant={delivery === "email" ? "default" : "outline"} size="sm" onClick={() => setDelivery("email")} className={delivery === "email" ? "bg-[color:var(--brand-ocean)] text-white" : ""}>
                <Mail className="w-3.5 h-3.5 mr-1" />Email
              </Button>
              <Button data-testid="api-sched-delivery-webhook" variant={delivery === "webhook" ? "default" : "outline"} size="sm" onClick={() => setDelivery("webhook")} className={delivery === "webhook" ? "bg-[color:var(--brand-ocean)] text-white" : ""}>
                <Webhook className="w-3.5 h-3.5 mr-1" />Webhook
              </Button>
            </div>
          </div>
          {delivery === "email" ? (
            <div><Label className="text-xs">Recipients (comma-separated)</Label>
              <Input data-testid="api-sched-recipients" value={recipients} onChange={(e) => setRecipients(e.target.value)} placeholder="data@partner.com, ops@partner.com" className="bg-[color:var(--surface)]" />
            </div>
          ) : (
            <>
              <div><Label className="text-xs">Webhook URL</Label>
                <Input data-testid="api-sched-webhook-url" value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} placeholder="https://partner.example.com/eip-ingest" className="bg-[color:var(--surface)]" />
              </div>
              <div><Label className="text-xs">Custom headers (one per line, &quot;Key: value&quot;)</Label>
                <Textarea data-testid="api-sched-webhook-headers" value={webhookHeaders} onChange={(e) => setWebhookHeaders(e.target.value)} rows={3} placeholder="X-API-Key: secret&#10;Authorization: Bearer …" className="bg-[color:var(--surface)] font-mono text-xs" />
              </div>
            </>
          )}
          <label className="flex items-center justify-between text-xs text-[color:var(--ink-2)] bg-[color:var(--surface-2)] rounded-lg px-3 py-2">
            <span>Enabled</span>
            <Switch data-testid="api-sched-enabled" checked={enabled} onCheckedChange={setEnabled} />
          </label>
        </div>
        <DialogFooter><Button data-testid="api-sched-save-btn" onClick={save} disabled={busy} className="bg-[color:var(--brand-ocean)] text-white">{isEdit ? "Save changes" : "Create schedule"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
